import { object, string, array, bool } from 'yup';
import { treatmentRecordCommonSchema } from './treatmentRecordCommon';
import s3jsonConstants from '../../constants/s3jsonConstants';
import { facialTreatmentRecordCommonSchema } from './facialTreatmentRecordCommon';
import { nullToEmptyString, nullToArray } from './transforms';

export const treatmentRecordSchema = object().shape({
  treatmentAttributes: object().shape({
    exfoliationHwiWandProduct: string()
      .when('exfoliationEnhancement', {
        is: (exfoliationEnhancement: string[]) =>
          exfoliationEnhancement?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: string().required('Exfoliation Enhancements Wand Product'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    exfoliationHwiTreatmentSerum: array(string())
      .when('exfoliationEnhancement', {
        is: (exfoliationEnhancement: string[]) =>
          exfoliationEnhancement?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: array(string())
          .required('Exfoliation Enhancements treatment serum')
          .min(1, 'Exfoliation Enhancements treatment serum')
          .max(2, 'Exfoliation Enhancements treatment serum'),
      })
      .nullable(true)
      .transform(nullToArray),
    exfoliationHwiSuctionRate: string()
      .when('exfoliationEnhancement', {
        is: (exfoliationEnhancement: string[]) =>
          exfoliationEnhancement?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: string().required('Exfoliation Enhancements Suction Rate'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    exfoliationPeelLayers: string()
      .when('exfoliationEnhancement', {
        is: (exfoliationEnhancement: string[]) =>
          exfoliationEnhancement?.includes(s3jsonConstants['Peel — $50']),
        then: string().required('Exfoliation Enhancements Peel Layers'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    extractionsPrepMethod: array(string())
      .when('extractionsSegment', {
        is: true,
        then: array(string())
          .required('Extraction Prep Products')
          .min(1, 'Extraction Prep Products'),
      })
      .nullable(true)
      .transform(nullToArray),
    touchPointSegment: bool().nullable(true),
    touchPointMassageMedium: string()
      .when('touchPointSegment', {
        is: true,
        then: string().required('Touch Point Massage Medium'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    maskHwiWandProduct: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: string().required('Mask Segment Wand Product'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskHwiTreatmentSerum: array(string())
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: array(string())
          .required('Mask Segment Treatment Serum')
          .min(1, 'Mask Segment Treatment Serum')
          .max(2, 'Mask Segment Treatment Serum'),
      })
      .nullable()
      .transform(nullToArray),
    maskHwiSuctionRate: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(
            s3jsonConstants['Hydro Wand Infusion — $80'],
          ),
        then: string().required('Mask Segment Suction Rate'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskMicrocurrentProduct: array(string())
      .when('maskEnhancements', {
        is: (maskEnhancements: string[]) =>
          maskEnhancements?.includes(s3jsonConstants['Microcurrent — $80']),
        then: array(string())
          .required('Mask Segment Microcurrent Product')
          .min(1, 'Mask Segment Microcurrent Product'),
      })
      .nullable(true)
      .transform(nullToArray),
    maskMicrocurrentSerum: array(string())
      .when('maskEnhancements', {
        is: (maskEnhancements: string[]) =>
          maskEnhancements?.includes(s3jsonConstants['Microcurrent — $80']),
        then: array(string())
          .required('Mask Segment Microcurrent Serum')
          .min(1, 'Mask Segment Microcurrent Serum'),
      })
      .nullable(true)
      .transform(nullToArray),
    maskMicrocurrentTreatmentTime: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[]) => {
          return maskEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: string().required('Mask Segment Microcurrent Treatment Time'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    maskNanoinfusionDeviceUsed: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
        then: string().required('Mask Segment Nano Infusion Device Used'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskNanoinfusionSerumUsed: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
        then: string().required('Mask Segment Nano Infusion Serum Used'),
      })
      .nullable()
      .transform(nullToEmptyString),
    maskNanoinfusionMaskUsed: string()
      .when('maskEnhancements', {
        is: (maskEnhancements: string[] | undefined) =>
          maskEnhancements?.includes(s3jsonConstants['Nano Infusion -$80']),
        then: string().required('Mask Segment Nano Infusion Mask Used'),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapySegment: bool().nullable(true),
    targetedTherapyEnhancements: array(string())
      .when('targetedTherapySegment', {
        is: true,
        then: array(string())
          .required('Targeted Therapy Enhancements')
          .min(1, 'Targeted Therapy Enhancements'),
      })
      .nullable(true)
      .transform(nullToArray),
    targetedTherapyPpMask: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Platinum Party – $65'],
          ),
        then: string().required('Targeted Therapy Platinum Party Mask'),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyPpMicrocurrentProgram: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Platinum Party – $65'],
          ),
        then: string().required(
          'Targeted Therapy Platinum Party Microcurrent Program',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyPpMicrocurrentIntensity: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Platinum Party – $65'],
          ),
        then: string().required(
          'Targeted Therapy Platinum Party Microcurrent Intensity',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyPpAdditionalTouchpoint: array(string())
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Platinum Party – $65'],
          ),
        then: array(string())
          .required('Targeted Therapy Platinum Party Additional Touchpoint')
          .min(1, 'Targeted Therapy Platinum Party Additional Touchpoint'),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyLedPanelTime: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['LED Panel — $50'],
          );
        },
        then: string().required('Targeted Therapy Enhancements LED panel Time'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    targetedTherapyLedTouchpoint: array(string())
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements?: string[]) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['LED Panel — $50'],
          ),
        then: array(string())
          .required('Targeted Therapy Enhancements LED Touchpoint')
          .min(1, 'Targeted Therapy Enhancements LED Touchpoint'),
      })
      .nullable(true)
      .transform(nullToArray),
    targetedTherapyMicrocurrentProgramLpi: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: string().required(
          'Targeted Therapy Microcurrent Long Program Intesity',
        ),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    targetedTherapyMicrocurrentProgramSpi: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: string().required(
          'Targeted Therapy Microcurrent Short Program Intensity',
        ),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    targetedTherapyMicrocurrentProduct: array(string())
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: array(string())
          .required('Targeted Therapy Microcurrent Product')
          .min(1, 'Targeted Therapy Microcurrent Product'),
      })
      .nullable(true)
      .transform(nullToArray),
    targetedTherapyMicrocurrentSerum: array(string())
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: array(string())
          .required('Targeted Therapy Microcurrent Serum')
          .min(1, 'Targeted Therapy Microcurrent Serum'),
      })
      .nullable(true)
      .transform(nullToArray),
    targetedTherapyMicrocurrentTreatmentTime: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[]) => {
          return targetedTherapyEnhancements?.includes(
            s3jsonConstants['Microcurrent — $80'],
          );
        },
        then: string().required('Targeted Therapy Microcurrent Treatment Time'),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    targetedTherapyNanoinfusionDeviceUsed: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required('Targeted Therapy Nano Infusion Device Used'),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyNanoinfusionSerumUsed: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required('Targeted Therapy Nano Infusion Serum Used'),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyNanoinfusionMaskUsed: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required('Targeted Therapy Nano Infusion Mask Used'),
      })
      .nullable()
      .transform(nullToEmptyString),

    targetedTherapyNanoinfusionStitchDepthFcc: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required(
          'Targeted Therapy Nano Mask Stitch depth for forehead, cheeks, and chin',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyNanoinfusionSpeedFcc: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required(
          'Targeted Therapy Nano Mask Speed for forehead, cheeks, and chin',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyNanoinfusionStitchDepthNe: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required(
          'Targeted Therapy Nano Mask Stitch depth for nose and eyes',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    targetedTherapyNanoinfusionSpeedNe: string()
      .when('targetedTherapyEnhancements', {
        is: (targetedTherapyEnhancements: string[] | undefined) =>
          targetedTherapyEnhancements?.includes(
            s3jsonConstants['Nano Infusion -$80'],
          ),
        then: string().required(
          'Targeted Therapy Nano Mask Speed for nose and eyes',
        ),
      })
      .nullable()
      .transform(nullToEmptyString),
    sosBaseProduct: string()
      .when(['sosSegment', 'sosBaseProductTp'], {
        is: (sosSegment: boolean, sosBaseProductTp: string) =>
          sosSegment && !sosBaseProductTp,
        then: string().required('SOS Base Product'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    choose_one: bool().test(
      'atleast_at least three',
      'Choose at least three segments',
      function () {
        const selectedSegment = [
          this.parent.exfoliationSegment,
          this.parent.extractionsSegment,
          this.parent.sosSegment,
          this.parent.touchPointSegment,
          this.parent.maskSegment,
          this.parent.targetedTherapySegment,
        ].filter((segment) => segment).length;
        return selectedSegment >= 2;
      },
    ),
    ...treatmentRecordCommonSchema.treatmentAttributes,
    ...facialTreatmentRecordCommonSchema,
  }),
  ...treatmentRecordCommonSchema.topLevel,
});
